/**
 * Modal form for creating a Page inside a Site. Posts to
 * `/api/admin/sites/:siteId/pages` which transactionally writes the Page
 * row and its DRAFT v1 PageVersion (spec §19 Page Lifecycle Summary).
 *
 * The shared `createPageSchema` is the single source of validation.
 */

import { useEffect } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useForm } from "react-hook-form";
import type { CreatePageInput, PageType } from "@ottopilot/shared";
import { createPageSchema } from "@ottopilot/shared";
import { useCreatePage } from "../../hooks/usePages";
import { useToast } from "../../components/ToastProvider";
import { makeZodResolver } from "../../lib/zodResolver";

const PAGE_TYPES: PageType[] = [
  "HOMEPAGE",
  "CONTACT",
  "BLOG_INDEX",
  "BLOG_ARTICLE",
  "PRODUCT_INDEX",
  "PRODUCT_DETAIL",
  "LANDING",
  "CUSTOM",
];

type Props = {
  open: boolean;
  onClose: () => void;
  siteId: string;
};

export function CreatePageDialog({ open, onClose, siteId }: Props): ReactElement {
  const create = useCreatePage(siteId);
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreatePageInput>({
    resolver: makeZodResolver(createPageSchema),
    defaultValues: { path: "/", type: "HOMEPAGE", title: "" },
  });

  useEffect(() => {
    if (open) reset({ path: "/", type: "HOMEPAGE", title: "" });
  }, [open, reset]);

  const onSubmit = handleSubmit((values) => {
    create.mutate(values, {
      onSuccess: (result) => {
        toast.showSuccess(`Created page ${result.page.path}.`);
        onClose();
      },
      onError: (err) => {
        toast.showError(err.message || "Could not create page.");
      },
    });
  });

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!create.isPending) onClose();
      }}
      maxWidth="sm"
      fullWidth
      data-testid="create-page-dialog"
    >
      <DialogTitle>New page</DialogTitle>
      <Stack
        component="form"
        onSubmit={(e) => {
          void onSubmit(e);
        }}
        noValidate
      >
        <DialogContent>
          <Stack spacing={2}>
            {create.isError ? <Alert severity="error">{create.error.message}</Alert> : null}
            <TextField
              label="Title"
              fullWidth
              autoFocus
              error={Boolean(errors.title)}
              helperText={errors.title?.message ?? "Visible page title."}
              inputProps={{ "data-testid": "create-page-title-input" }}
              {...register("title")}
            />
            <TextField
              label="Path"
              fullWidth
              error={Boolean(errors.path)}
              helperText={errors.path?.message ?? "Lowercase URL path. / for the homepage."}
              inputProps={{ "data-testid": "create-page-path-input" }}
              {...register("path")}
            />
            <TextField
              select
              label="Type"
              fullWidth
              defaultValue="HOMEPAGE"
              error={Boolean(errors.type)}
              helperText={errors.type?.message}
              SelectProps={{
                // Apply the testid to the visible Select trigger (a `<div>`)
                // so Playwright can click it to open the dropdown. MUI's
                // `SelectDisplayProps` type does not enumerate `data-*`
                // attributes; the cast is confined to this single attribute.
                SelectDisplayProps: { "data-testid": "create-page-type-select" } as Record<
                  string,
                  string
                >,
              }}
              {...register("type")}
            >
              {PAGE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            disabled={create.isPending}
            data-testid="create-page-cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || create.isPending}
            data-testid="create-page-submit-button"
          >
            {create.isPending ? "Creating..." : "Create page"}
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
}
